import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Barcode, Layers, Plus, ScanLine, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import type { StockItem, StockLotsResponse } from '@/types'
import { Button } from '@/components/ui/button'
import { fmtQty } from '@/lib/quantity'
import { formatDateHuman } from '@/lib/utils'
import { purchaseUnitOf } from '@/lib/purchaseUnits'
import { canonicalBarcode } from '@/lib/barcode'
import { IM, lotName } from './itemMasterStrings'
import { BarcodeScanDialog } from './BarcodeScanDialog'

/**
 * Kitchen-flow (Lane A) blocks for the item drawer: how the item is bought,
 * its barcodes, and its open batches in the order they will be used. The
 * drawer renders these only when useKitchenFlowEnabled() is true.
 */

export function PurchaseUnitLine({ item }: { item: Pick<StockItem, 'unit' | 'purchase_unit_label' | 'purchase_unit_factor'> }) {
  const pu = purchaseUnitOf(item)
  if (!pu) return null
  return <p className="text-sm"><span className="text-muted-foreground">{IM.purchaseUnit.heading}:</span> {IM.purchaseUnit.shown(pu.label, fmtQty(pu.factor), item.unit)}</p>
}

export function ItemBarcodesSection({ item, showToast }: { item: StockItem; showToast: (t: 'success' | 'error', m: string) => void }) {
  const qc = useQueryClient()
  const key = ['stockItemBarcodes', item.id]
  const { data } = useQuery({ queryKey: key, queryFn: () => apiClient.listStockItemBarcodes(item.id).then((r) => r.data ?? []) })
  const codes = data ?? []
  const [adding, setAdding] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  const [form, setForm] = useState({ barcode: '', pack_qty: '1', pack_label: '' })

  const add = useMutation({
    mutationFn: () => apiClient.addStockItemBarcode(item.id, {
      barcode: form.barcode,
      pack_qty: Number(form.pack_qty) || 1,
      pack_label: form.pack_label || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
      showToast('success', IM.barcodes.added)
      setForm({ barcode: '', pack_qty: '1', pack_label: '' })
      setAdding(false)
    },
    onError: (e: Error) => showToast('error', e.message),
  })
  const remove = useMutation({
    mutationFn: (barcodeId: string) => apiClient.deleteStockItemBarcode(item.id, barcodeId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); showToast('success', IM.barcodes.removed) },
    onError: (e: Error) => showToast('error', e.message),
  })

  const valid = Boolean(canonicalBarcode(form.barcode)) && Number(form.pack_qty) > 0

  return (
    <div className="rounded-lg border p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold"><Barcode className="h-4 w-4" /> {IM.barcodes.heading}</h4>
        {!adding && (
          <Button size="sm" variant="outline" className="h-9" onClick={() => setAdding(true)}>
            <Plus className="mr-1 h-4 w-4" /> {IM.barcodes.add}
          </Button>
        )}
      </div>
      {codes.length === 0 && !adding && <p className="text-sm text-muted-foreground">{IM.barcodes.none}</p>}
      <ul className="space-y-1.5">
        {codes.map((b) => (
          <li key={b.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0">
              <span className="font-mono tabular-nums">{b.barcode}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {b.pack_label ? `${b.pack_label} · ` : ''}{IM.barcodes.perScan(fmtQty(b.pack_qty), item.unit)}
              </span>
            </span>
            <Button size="sm" variant="ghost" className="h-9 w-9 shrink-0 px-0" aria-label={IM.barcodes.remove}
              disabled={remove.isPending} onClick={() => remove.mutate(b.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
      {adding && (
        <form className="mt-2 space-y-2" onSubmit={(e) => { e.preventDefault(); if (valid) add.mutate() }}>
          <label className="block text-sm font-medium">{IM.barcodes.codeLabel}</label>
          <div className="flex gap-2">
            <input className="h-10 min-w-0 flex-1 rounded-md border bg-background px-3 font-mono text-sm" inputMode="numeric" autoComplete="off"
              value={form.barcode} onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))} autoFocus />
            <Button type="button" variant="outline" onClick={() => setScanOpen(true)} aria-label={IM.scan.button}>
              <ScanLine className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-sm">
              <span className="font-medium">{IM.barcodes.packLabel}</span>
              <input type="number" min="0" step="any" className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.pack_qty} onChange={(e) => setForm((f) => ({ ...f, pack_qty: e.target.value }))} />
            </label>
            <label className="block text-sm">
              <span className="font-medium">{IM.barcodes.packNameLabel}</span>
              <input className="mt-1 h-10 w-full rounded-md border bg-background px-3 text-sm" placeholder={IM.barcodes.packNamePlaceholder}
                value={form.pack_label} onChange={(e) => setForm((f) => ({ ...f, pack_label: e.target.value }))} />
            </label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={!valid || add.isPending}>{IM.barcodes.add}</Button>
            <Button type="button" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </form>
      )}
      <BarcodeScanDialog open={scanOpen} onClose={() => setScanOpen(false)}
        onCode={(code) => { setForm((f) => ({ ...f, barcode: code })); setScanOpen(false) }} />
    </div>
  )
}

/**
 * "Use batch X first — expires in N days", or nothing when there is no choice.
 * Expired lots are never "use first"; they get their own "throw it away" line.
 */
export function FefoHint({ lots, unit = '', className = '' }: { lots: StockLotsResponse | undefined; unit?: string; className?: string }) {
  const first = lots?.use_first
  const expired = lots?.expired_qty ?? 0
  if (!first && !(expired > 0 && lots?.expired_first)) return null
  const urgent = first?.days_until_expiry != null && first.days_until_expiry <= 2
  return (
    <>
      {first && (
        <p className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium ${urgent ? 'bg-danger-surface/50 text-danger-fg' : 'bg-warning-surface/50 text-warning-fg'}  ${className}`}>
          <Layers className="h-4 w-4 shrink-0" /> {IM.lots.useFirst(lotName(first), first.days_until_expiry)}
        </p>
      )}
      {expired > 0 && lots?.expired_first && (
        <p className={`flex items-center gap-1.5 rounded-md bg-danger-surface/50 px-3 py-2 text-sm font-medium text-danger-fg ${className}`}>
          <Layers className="h-4 w-4 shrink-0" />{' '}
          {IM.lots.expiredThrowAway(fmtQty(expired), unit, formatDateHuman(lots.expired_first), formatDateHuman(lots.expired_last ?? lots.expired_first))}
        </p>
      )}
    </>
  )
}

export function useStockItemLots(itemId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['stockItemLots', itemId],
    queryFn: () => apiClient.getStockItemLots(itemId).then((r) => r.data),
    enabled,
  })
}
